import { Box, Container, Typography, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import LayersIcon from "@mui/icons-material/Layers";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import NatureIcon from "@mui/icons-material/Nature";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ChairIcon from "@mui/icons-material/Chair";
import WeekendIcon from "@mui/icons-material/Weekend";
import SectionTitle from "../components/SectionTitle";

const SERVICES = [
  { icon: CleaningServicesIcon, title: "Nettoyage en Profondeur", description: "Extraction avec machines professionnelles pour un nettoyage en profondeur et un séchage rapide." },
  { icon: NatureIcon, title: "Produits Écologiques", description: "Utilisation de produits écologiques pour un nettoyage sûr et respectueux de l'environnement." },
  { icon: LocalShippingIcon, title: "Ramassage & Livraison", description: "Collecte et livraison de vos tapis et mobilier directement à votre hôtel, riad ou restaurant." },
  { icon: ChairIcon, title: "Mobilier & Canapés", description: "Nettoyage professionnel de canapés, fauteuils et mobilier rembourrés avec soin." },
  { icon: WeekendIcon, title: "Moquettes & Revêtements", description: "Entretien complet de vos moquettes et revêtements de sol pour hôtels et restaurants." },
  { icon: LayersIcon, title: "Tapis Orientaux", description: "Traitement spécialisé pour tapis orientaux, berbères et pièces de collection." },
];

const HeroBanner = () => (
  <Box sx={{
    position: "relative", height: { xs: "60vh", md: "80vh" },
    display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", overflow: "hidden",
    background: 'url("/images/carpet.png") center/cover no-repeat',
    "&::before": { content: '""', position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(10,6,2,0.6) 0%, rgba(10,6,2,0.4) 50%, rgba(10,6,2,0.85) 100%)", zIndex: 1 },
  }}>
    <Container maxWidth="md" sx={{ position: "relative", zIndex: 2, px: { xs: 2, md: 3 } }}>
      <Typography sx={{ color: "secondary.main", fontSize: "0.68rem", letterSpacing: "0.3em", textTransform: "uppercase", fontWeight: 700, mb: 1.5 }}>Services</Typography>
      <Typography variant="h1" sx={{ color: "#fff", fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: { xs: "1.6rem", md: "3rem" }, mb: 2, lineHeight: 1.1 }}>
        Nettoyage de Tapis & Mobilier
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, mb: 2 }}>
        <Box sx={{ width: 50, height: "1px", backgroundColor: "rgba(201,168,76,0.5)" }} />
        <Box sx={{ width: 5, height: 5, backgroundColor: "secondary.main", transform: "rotate(45deg)" }} />
        <Box sx={{ width: 50, height: "1px", backgroundColor: "rgba(201,168,76,0.5)" }} />
      </Box>
      <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: { xs: "0.85rem", md: "1rem" }, lineHeight: 1.8, maxWidth: 520, mx: "auto" }}>
        Entretien haut de gamme pour hôtels, riads et restaurants à Marrakech.
      </Typography>
    </Container>
    <Box sx={{ position: "absolute", bottom: -1, left: 0, right: 0, zIndex: 3, lineHeight: 0 }}>
      <svg viewBox="0 0 1440 50" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{ display: "block", width: "100%", height: 50 }}>
        <path d="M0,30 C360,55 1080,5 1440,30 L1440,50 L0,50 Z" fill="#f5f0e8" />
      </svg>
    </Box>
  </Box>
);

const ServiceCard = ({ icon: Icon, title, description }) => (
  <Box sx={{
    backgroundColor: "#fff", border: "1px solid rgba(201,168,76,0.2)", borderRadius: "8px",
    p: { xs: 2.5, md: 3.5 }, textAlign: "center",
    flex: { xs: "1 1 calc(50% - 8px)", md: "1 1 calc(33.333% - 16px)" },
    maxWidth: { xs: "calc(50% - 8px)", md: "calc(33.333% - 16px)" },
    minWidth: { xs: "140px", md: "200px" },
    display: "flex", flexDirection: "column", alignItems: "center",
    boxShadow: "0 2px 16px rgba(26,17,8,0.07)",
    transition: "all 0.3s ease",
    "&:hover": { transform: "translateY(-5px)", boxShadow: "0 12px 36px rgba(26,17,8,0.13)", borderColor: "secondary.main" },
  }}>
    <Box sx={{ width: { xs: 60, md: 80 }, height: { xs: 60, md: 80 }, borderRadius: "50%", mb: 2, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid rgba(201,168,76,0.3)", background: "radial-gradient(circle, rgba(201,168,76,0.1) 0%, transparent 70%)" }}>
      <Icon sx={{ fontSize: { xs: 28, md: 40 }, color: "rgba(201,168,76,0.8)" }} />
    </Box>
    <Typography sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 600, fontSize: { xs: "0.85rem", md: "1rem" }, color: "text.primary", mb: 1 }}>{title}</Typography>
    <Box sx={{ width: 25, height: "1px", backgroundColor: "rgba(201,168,76,0.4)", mb: 1.2 }} />
    <Typography sx={{ color: "text.secondary", fontSize: { xs: "0.75rem", md: "0.86rem" }, lineHeight: 1.7 }}>{description}</Typography>
  </Box>
);

const BottomCTA = () => {
  const navigate = useNavigate();
  return (
    <Box sx={{ backgroundColor: "#0d0a04", py: { xs: 7, md: 10 }, textAlign: "center" }}>
      <Container maxWidth="md" sx={{ px: { xs: 2, md: 3 } }}>
        <LayersIcon sx={{ fontSize: { xs: 32, md: 40 }, color: "secondary.main", mb: 2 }} />
        <Typography variant="h3" sx={{ color: "#fff", fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: { xs: "1.3rem", md: "2.2rem" }, mb: 2, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Tapis, Moquettes & Mobilier
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, mb: 3 }}>
          <Box sx={{ width: 50, height: "1px", backgroundColor: "rgba(201,168,76,0.4)" }} />
          <Box sx={{ width: 5, height: 5, backgroundColor: "secondary.main", transform: "rotate(45deg)" }} />
          <Box sx={{ width: 50, height: "1px", backgroundColor: "rgba(201,168,76,0.4)" }} />
        </Box>
        <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: { xs: "0.88rem", md: "1rem" }, lineHeight: 1.9, maxWidth: 520, mx: "auto", mb: 4 }}>
          Nous intervenons directement dans vos établissements pour nettoyer tapis, moquettes, canapés et fauteuils.
        </Typography>
        <Button onClick={() => navigate("/contact")} variant="outlined" sx={{ borderColor: "secondary.main", color: "secondary.main", fontSize: "0.78rem", letterSpacing: "0.18em", px: 5, py: 1.6, borderRadius: 0, fontWeight: 700, transition: "all 0.3s ease", "&:hover": { backgroundColor: "secondary.main", color: "#1a0f05" } }}>
          Demander un Devis
        </Button>
      </Container>
    </Box>
  );
};

const TapisPage = () => (
  <Box>
    <HeroBanner />
    <Box sx={{ py: { xs: 6, md: 10 }, backgroundColor: "#f5f0e8", position: "relative", "&::before": { content: '""', position: "absolute", inset: 0, background: 'url("/images/flowers.jpg") center/cover no-repeat', opacity: 0.03, pointerEvents: "none", zIndex: 0 } }}>
      <Container sx={{ position: "relative", zIndex: 1, px: { xs: 2, md: 3 } }}>
        <SectionTitle overline="Nos Prestations" title="Nos Services" align="center" />
        <Box sx={{ display: "flex", gap: { xs: 2, md: 3 }, flexWrap: "wrap", mt: { xs: 3, md: 5 } }}>
          {SERVICES.map((service) => (
            <ServiceCard key={service.title} {...service} />
          ))}
        </Box>
      </Container>
    </Box>
    <BottomCTA />
  </Box>
);

export default TapisPage;